import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { notificationApi, type MailConfigStatus } from "@/lib/notification.api";
import { queryKeys } from "@/lib/query-keys";

export function useMailTest() {
  const queryClient = useQueryClient();
  const [to, setTo] = useState("");
  const [status, setStatus] = useState<MailConfigStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"success" | "warning" | "danger">("warning");

  async function checkStatus() {
    setChecking(true);
    setMessage(null);
    try {
      const response = await notificationApi.mailStatus();
      setStatus(response.data);
      setMessageTone(response.data.transportOk ? "success" : "warning");
      setMessage(response.data.transportOk ? "Connexion SMTP valide." : "Configuration incomplète.");
    } catch (error: unknown) {
      const data = (error as { response?: { data?: MailConfigStatus } })?.response?.data;
      if (data) setStatus(data);
      setMessageTone("danger");
      setMessage(data?.message ?? "Impossible de vérifier la configuration SMTP.");
    } finally {
      setChecking(false);
    }
  }

  async function sendTest() {
    setSending(true);
    setMessage(null);
    try {
      const response = await notificationApi.sendTestMail(to);
      if (response.data.success) {
        setMessageTone(response.data.rejected.length > 0 ? "warning" : "success");
        setMessage(`Email envoyé à ${response.data.accepted.join(", ")}.`);
      } else {
        setMessageTone("danger");
        setMessage(response.data.errorMessage ?? "Email refusé par le serveur SMTP.");
      }
    } catch (error: unknown) {
      setMessageTone("danger");
      setMessage(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Erreur lors de l'envoi du test.",
      );
    } finally {
      queryClient.invalidateQueries({ queryKey: queryKeys.mailOutbox() });
      setSending(false);
    }
  }

  return {
    to,
    setTo,
    status,
    checking,
    sending,
    message,
    messageTone,
    checkStatus,
    sendTest,
  };
}
